import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../store/store';
import { forumAPI } from '../services/api';
import ReactQuillEditor from '../components/ReactQuillEditor';
import GuestAuthBanner from '../components/GuestAuthBanner';
import {
  MessageSquare,
  Send,
  Loader2,
  Image as ImageIcon,
  X,
  Plus,
  Trash2,
  Search,
  TrendingUp,
  Clock,
  Users,
  Filter,
  ChevronDown,
  Flame,
  Heart,
  LayoutGrid,
  List
} from 'lucide-react';

interface User {
  _id: string;
  name: string;
  email: string;
  role?: string;
}

interface Post {
  _id: string;
  author: User;
  content: string;
  images?: string[];
  likes: Array<{ user: User; createdAt: string }>;
  comments: Array<{ _id?: string; user: User; content: string; createdAt: string }>;
  shares: Array<{ user: User; createdAt: string }>;
  tags?: string[];
  category?: string;
  createdAt: string;
}

interface Category {
  name: string;
  count: number;
  description?: string;
  image?: string;
}

const agricultureCategories = [
  'Crop Production', 'Livestock & Poultry', 'Agricultural Machinery',
  'Irrigation & Water Management', 'Soil & Fertilizers', 'Plant Diseases & Pest Control',
  'Agro-industry & Processing', 'Market Prices & Trade', 'Organic Farming', 'Agricultural Innovations'
];

const defaultCategorySet = new Set([
  'General', 'Business', 'Agriculture', 'Technology', 'News',
  'Discussion', 'Question', 'Announcement', 'Event', 'Other',
  ...agricultureCategories
]);

const Forum: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [postContent, setPostContent] = useState('');
  const [postImages, setPostImages] = useState<string[]>([]);
  const [postTags, setPostTags] = useState<string[]>([]);
  const [postCategory, setPostCategory] = useState<string>('General');
  const [tagInput, setTagInput] = useState('');
  const [expandedCardCategory, setExpandedCardCategory] = useState<string | null>(null);
  const [cardPostContent, setCardPostContent] = useState<{ [key: string]: string }>({});
  const [cardPostImages, setCardPostImages] = useState<{ [key: string]: string[] }>({});
  const [cardPostTags, setCardPostTags] = useState<{ [key: string]: string[] }>({});
  const [cardTagInput, setCardTagInput] = useState<{ [key: string]: string }>({});
  const [creatingCardPost, setCreatingCardPost] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [showCreateCategory, setShowCreateCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryDescription, setNewCategoryDescription] = useState('');
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'posts' | 'recent'>('posts');
  const [viewMode, setViewMode] = useState<'table' | 'cards'>('table');
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const filteredCategories = useMemo(() => {
    let result = [...categories];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(c =>
        c.name.toLowerCase().includes(q) ||
        (c.description || getCategoryDescription(c.name)).toLowerCase().includes(q)
      );
    }
    switch (sortBy) {
      case 'name':
        result.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'posts':
        result.sort((a, b) => (b.count || 0) - (a.count || 0));
        break;
      case 'recent':
        result.sort((a, b) => {
          const aLast = posts.filter(p => (p.category || 'General') === a.name).sort((x, y) => new Date(y.createdAt).getTime() - new Date(x.createdAt).getTime())[0];
          const bLast = posts.filter(p => (p.category || 'General') === b.name).sort((x, y) => new Date(y.createdAt).getTime() - new Date(x.createdAt).getTime())[0];
          if (!aLast && !bLast) return 0;
          if (!aLast) return 1;
          if (!bLast) return -1;
          return new Date(bLast.createdAt).getTime() - new Date(aLast.createdAt).getTime();
        });
        break;
    }
    return result;
  }, [categories, searchQuery, sortBy, posts]);

  const totalPosts = useMemo(() => posts.length, [posts]);
  const totalCategories = useMemo(() => categories.length, [categories]);
  const totalAuthors = useMemo(() => new Set(posts.map(p => p.author?._id).filter(Boolean)).size, [posts]);

  useEffect(() => {
    loadCategories();
    loadPosts();
  }, []);

  const loadCategories = async () => {
    setLoadingCategories(true);
    try {
      const response = await forumAPI.getCategories();
      if (response.data.success) {
        const sorted = response.data.data.sort((a: Category, b: Category) => {
          const isACustom = !agricultureCategories.includes(a.name) && !defaultCategorySet.has(a.name);
          const isBCustom = !agricultureCategories.includes(b.name) && !defaultCategorySet.has(b.name);
          if (isACustom && !isBCustom) return -1;
          if (!isACustom && isBCustom) return 1;
          return b.count - a.count;
        });
        setCategories(sorted);
      } else {
        const fallback = ['General', 'Business', 'Agriculture', 'Technology', 'News', 'Discussion', 'Question', 'Announcement', 'Event', 'Other', ...agricultureCategories];
        setCategories(fallback.map(n => ({ name: n, count: 0 })));
      }
    } catch {
      setCategories([
        { name: 'General', count: 0 }, { name: 'Business', count: 0 }, { name: 'Agriculture', count: 0 },
        { name: 'Technology', count: 0 }, { name: 'News', count: 0 }, { name: 'Discussion', count: 0 },
        { name: 'Question', count: 0 }, { name: 'Announcement', count: 0 }, { name: 'Event', count: 0 },
        { name: 'Other', count: 0 }, ...agricultureCategories.map(n => ({ name: n, count: 0 }))
      ]);
    } finally {
      setLoadingCategories(false);
    }
  };

  const loadPosts = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await forumAPI.getPosts({ page: 1, limit: 20 });
      if (response.data.success) setPosts(response.data.data);
      else setError(response.data.message || 'Failed to load posts');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryCardClick = (categoryName: string, e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('.create-post-form') || target.closest('.create-post-button')) return;
    navigate(`/forum/category/${encodeURIComponent(categoryName)}`);
  };

  const handleCardCreatePost = (categoryName: string) => {
    if (!user) { setError('You must be logged in to create a post'); return; }
    setExpandedCardCategory(expandedCardCategory === categoryName ? null : categoryName);
    if (!cardPostContent[categoryName]) setCardPostContent(prev => ({ ...prev, [categoryName]: '' }));
    if (!cardPostImages[categoryName]) setCardPostImages(prev => ({ ...prev, [categoryName]: [] }));
    if (!cardPostTags[categoryName]) setCardPostTags(prev => ({ ...prev, [categoryName]: [] }));
  };

  const handleCardImageUpload = (categoryName: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length) return;
    Array.from(files).forEach((file) => {
      if (file.size > 5 * 1024 * 1024) { setError('Image too large (max 5MB)'); return; }
      const reader = new FileReader();
      reader.onloadend = () => {
        setCardPostImages(prev => ({ ...prev, [categoryName]: [...(prev[categoryName] || []), reader.result as string] }));
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const addCardTag = (categoryName: string) => {
    const input = cardTagInput[categoryName] || '';
    if (input.trim() && !(cardPostTags[categoryName] || []).includes(input.trim())) {
      setCardPostTags(prev => ({ ...prev, [categoryName]: [...(prev[categoryName] || []), input.trim()] }));
      setCardTagInput(prev => ({ ...prev, [categoryName]: '' }));
    }
  };

  const removeCardTag = (categoryName: string, tag: string) => {
    setCardPostTags(prev => ({ ...prev, [categoryName]: (prev[categoryName] || []).filter(t => t !== tag) }));
  };

  const handleCreateCardPost = async (categoryName: string) => {
    const content = cardPostContent[categoryName] || '';
    const images = cardPostImages[categoryName] || [];
    const tags = cardPostTags[categoryName] || [];
    const textContent = content.replace(/<[^>]*>/g, '').trim();
    if (!textContent && images.length === 0) { setError('Please add content or images'); return; }
    if (!user) { setError('You must be logged in'); return; }

    setCreatingCardPost(categoryName);
    setError(null);
    try {
      const response = await forumAPI.createPost({ content, images, tags, category: categoryName });
      if (response.data.success) {
        setCardPostContent(prev => ({ ...prev, [categoryName]: '' }));
        setCardPostImages(prev => ({ ...prev, [categoryName]: [] }));
        setCardPostTags(prev => ({ ...prev, [categoryName]: [] }));
        setCardTagInput(prev => ({ ...prev, [categoryName]: '' }));
        setExpandedCardCategory(null);
        loadPosts();
        loadCategories();
      } else setError(response.data.message || 'Failed to create post');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create post');
    } finally {
      setCreatingCardPost(null);
    }
  };

  const getCategoryIcon = (name: string) => {
    const map: Record<string, string> = {
      'Crop Production': '🌾', 'Livestock & Poultry': '🐄', 'Agricultural Machinery': '🚜',
      'Irrigation & Water Management': '💧', 'Soil & Fertilizers': '🌱', 'Plant Diseases & Pest Control': '🐛',
      'Agro-industry & Processing': '🏭', 'Market Prices & Trade': '📊', 'Organic Farming': '🌿',
      'Agricultural Innovations': '🤖'
    };
    return map[name] || '📝';
  };

  const getCategoryDescription = (name: string) => {
    const map: Record<string, string> = {
      'Crop Production': 'Wheat, Barley, Vegetables, Fruits',
      'Livestock & Poultry': 'Cattle, Poultry, Animal Husbandry',
      'Agricultural Machinery': 'Tractors, Equipment, Tools',
      'Irrigation & Water Management': 'Water Systems, Irrigation Techniques',
      'Soil & Fertilizers': 'Soil Health, Nutrients, Fertilizers',
      'Plant Diseases & Pest Control': 'Disease Management, Pest Control',
      'Agro-industry & Processing': 'Food Processing, Value Addition',
      'Market Prices & Trade': 'Market Trends, Pricing, Trading',
      'Organic Farming': 'Organic Practices, Certification',
      'Agricultural Innovations': 'Drones, IoT, Sensors, Technology'
    };
    return map[name] || 'Share your thoughts and connect with the community';
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length) return;
    Array.from(files).forEach((file) => {
      if (file.size > 5 * 1024 * 1024) { setError('Image too large (max 5MB)'); return; }
      const reader = new FileReader();
      reader.onloadend = () => setPostImages(prev => [...prev, reader.result as string]);
      reader.readAsDataURL(file);
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleCreatePost = async () => {
    const textContent = postContent.replace(/<[^>]*>/g, '').trim();
    if (!textContent && postImages.length === 0) { setError('Please add content or images'); return; }
    if (!user) { setError('You must be logged in'); return; }

    setCreating(true);
    setError(null);
    try {
      const response = await forumAPI.createPost({
        content: postContent,
        images: postImages,
        tags: postTags,
        category: postCategory
      });
      if (response.data.success) {
        setPostContent('');
        setPostImages([]);
        setPostTags([]);
        setPostCategory('General');
        setTagInput('');
        setShowCreatePost(false);
        loadPosts();
        loadCategories();
      } else setError(response.data.message || 'Failed to create post');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create post');
    } finally {
      setCreating(false);
    }
  };

  const addTag = () => {
    if (tagInput.trim() && !postTags.includes(tagInput.trim())) {
      setPostTags([...postTags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const removeTag = (tag: string) => setPostTags(postTags.filter(t => t !== tag));

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) { setError('Category name is required'); return; }
    if (!user || user.role !== 'admin') { setError('Only admins can create categories'); return; }

    setCreatingCategory(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('name', newCategoryName.trim());
      if (newCategoryDescription.trim()) fd.append('description', newCategoryDescription.trim());
      const response = await forumAPI.createCategory(fd);
      if (response.data.success) {
        setNewCategoryName('');
        setNewCategoryDescription('');
        setShowCreateCategory(false);
        loadCategories();
      } else setError(response.data.message || 'Failed to create category');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create category');
    } finally {
      setCreatingCategory(false);
    }
  };

  const handleDeleteCategory = async (categoryName: string) => {
    if (!user || user.role !== 'admin') { setError('Only admins can delete categories'); return; }
    if (!window.confirm(`Delete category "${categoryName}"? Posts will be moved to "General".`)) return;
    setError(null);
    try {
      const response = await forumAPI.deleteCategory(categoryName);
      if (response.data.success) {
        await loadCategories();
        await loadPosts();
      } else setError(response.data.message || 'Failed to delete category');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete category');
    }
  };

  const formatTimeAgo = (date: string) => {
    const diff = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return new Date(date).toLocaleDateString();
  };

  const isCustomCategoryName = (name: string) => !defaultCategorySet.has(name);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-emerald-50/30 to-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-emerald-600 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">Loading forum...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-emerald-50/30 to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <GuestAuthBanner message="Sign up or log in to create posts, comment, and engage with the community!" />

        {/* Forum Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2.5 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl shadow-lg shadow-emerald-200">
              <MessageSquare className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Community Forum</h1>
              <p className="text-sm text-gray-500">Discuss, share ideas, and connect with the community</p>
            </div>
          </div>

          {/* Stats Bar */}
          <div className="grid grid-cols-3 gap-3 mt-4">
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-3 flex items-center gap-3">
              <div className="p-2 bg-emerald-50 rounded-lg">
                <MessageSquare className="h-4 w-4 text-emerald-600" />
              </div>
              <div>
                <p className="text-lg font-bold text-gray-900">{totalPosts}</p>
                <p className="text-xs text-gray-500">Total Posts</p>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-3 flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <Filter className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <p className="text-lg font-bold text-gray-900">{totalCategories}</p>
                <p className="text-xs text-gray-500">Categories</p>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-3 flex items-center gap-3">
              <div className="p-2 bg-purple-50 rounded-lg">
                <Users className="h-4 w-4 text-purple-600" />
              </div>
              <div>
                <p className="text-lg font-bold text-gray-900">{totalAuthors}</p>
                <p className="text-xs text-gray-500">Contributors</p>
              </div>
            </div>
          </div>
        </div>

        {/* Search & Sort Bar */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search categories..."
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 shadow-sm"
            />
          </div>
          <div className="relative">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'name' | 'posts' | 'recent')}
              className="appearance-none pl-4 pr-10 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 shadow-sm cursor-pointer"
            >
              <option value="posts">Most Posts</option>
              <option value="recent">Most Recent</option>
              <option value="name">Alphabetical</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          </div>
          {/* View Toggle */}
          <div className="flex items-center bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
            <button
              onClick={() => setViewMode('table')}
              className={`p-2.5 flex items-center justify-center transition-colors ${viewMode === 'table' ? 'bg-emerald-600 text-white' : 'text-gray-500 hover:bg-gray-50'}`}
              title="Table view"
            >
              <List className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('cards')}
              className={`p-2.5 flex items-center justify-center transition-colors ${viewMode === 'cards' ? 'bg-emerald-600 text-white' : 'text-gray-500 hover:bg-gray-50'}`}
              title="Card view"
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
          </div>
          {user?.role === 'admin' && (
            <button
              onClick={() => setShowCreateCategory(!showCreateCategory)}
              className="inline-flex items-center px-4 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors text-sm font-medium shadow-sm"
            >
              <Plus className="h-4 w-4 mr-2" />
              {showCreateCategory ? 'Cancel' : 'New Category'}
            </button>
          )}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-start gap-3">
            <div className="p-1 bg-red-100 rounded-lg">
              <X className="h-4 w-4 text-red-600" />
            </div>
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {showCreateCategory && user?.role === 'admin' && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6">
            <h3 className="text-base font-semibold text-gray-900 mb-4">Create Category</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category name *</label>
                <input
                  type="text"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder="Enter category name"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
                <textarea
                  value={newCategoryDescription}
                  onChange={(e) => setNewCategoryDescription(e.target.value)}
                  placeholder="Enter description"
                  rows={3}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleCreateCategory}
                  disabled={creatingCategory || !newCategoryName.trim()}
                  className="px-5 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 disabled:opacity-50 flex items-center text-sm font-medium shadow-sm"
                >
                  {creatingCategory ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                  Create Category
                </button>
                <button
                  onClick={() => { setShowCreateCategory(false); setNewCategoryName(''); setNewCategoryDescription(''); }}
                  className="px-5 py-2.5 border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50 text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Category Display */}
        {loadingCategories ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
          </div>
        ) : filteredCategories.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-12 text-center">
            <Search className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No categories found</h3>
            <p className="text-gray-500 text-sm">Try adjusting your search query</p>
          </div>
        ) : viewMode === 'table' ? (
          /* Table View */
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white">
                  <th className="text-left px-5 py-3.5 text-sm font-semibold">Category</th>
                  <th className="text-center px-4 py-3.5 text-sm font-semibold w-24">Threads</th>
                  <th className="text-center px-4 py-3.5 text-sm font-semibold w-24">Likes</th>
                  <th className="text-left px-4 py-3.5 text-sm font-semibold w-52">Last Post</th>
                  {user?.role === 'admin' && <th className="w-12 py-3.5"></th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredCategories.map((category) => {
                  const catName = category.name;
                  const isCustom = isCustomCategoryName(catName);
                  const categoryPosts = posts.filter(p => (p.category || 'General') === catName);
                  const categoryLikes = categoryPosts.reduce((sum, p) => sum + (p.likes?.length || 0), 0);
                  const lastPost = categoryPosts.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];

                  return (
                    <tr
                      key={catName}
                      className="hover:bg-emerald-50/50 transition-colors cursor-pointer group"
                      onClick={() => navigate(`/forum/category/${encodeURIComponent(catName)}`)}
                    >
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-50 to-emerald-100 border border-emerald-200 flex items-center justify-center flex-shrink-0 text-lg group-hover:from-emerald-100 group-hover:to-emerald-200 transition-colors">
                            {category.image ? (
                              <img src={category.image} alt={catName} className="w-full h-full object-cover rounded-lg" />
                            ) : (
                              <span>{getCategoryIcon(catName)}</span>
                            )}
                          </div>
                          <div className="min-w-0">
                            <h3 className="font-semibold text-gray-900 group-hover:text-emerald-700 transition-colors">{catName}</h3>
                            <p className="text-xs text-gray-500 truncate max-w-xs">
                              {category.description?.trim() || getCategoryDescription(catName)}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="text-center px-4 py-4">
                        <span className="inline-flex items-center gap-1 text-sm font-medium text-gray-700">
                          <MessageSquare className="h-3.5 w-3.5 text-emerald-500" />
                          {Math.max(0, category.count || 0)}
                        </span>
                      </td>
                      <td className="text-center px-4 py-4">
                        <span className="inline-flex items-center gap-1 text-sm font-medium text-gray-700">
                          <Heart className="h-3.5 w-3.5 text-rose-400" />
                          {categoryLikes}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        {lastPost ? (
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-emerald-400 to-blue-500 flex items-center justify-center flex-shrink-0">
                              <span className="text-[10px] font-bold text-white">{lastPost.author?.name?.charAt(0).toUpperCase() || '?'}</span>
                            </div>
                            <div className="min-w-0">
                              <p className="text-xs font-medium text-gray-700 truncate">{lastPost.author?.name || 'Unknown'}</p>
                              <p className="text-[11px] text-gray-400">{formatTimeAgo(lastPost.createdAt)}</p>
                            </div>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">No posts yet</span>
                        )}
                      </td>
                      {user?.role === 'admin' && (
                        <td className="px-2 py-4">
                          {isCustom && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleDeleteCategory(catName); }}
                              className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg hover:bg-red-50 transition-all"
                              title="Delete category"
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          /* Card View */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredCategories.map((category) => {
              const catName = category.name;
              const isExpanded = expandedCardCategory === catName;
              const isCustom = isCustomCategoryName(catName);
              const lastPost = posts
                .filter(p => (p.category || 'General') === catName)
                .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
              const categoryPosts = posts.filter(p => (p.category || 'General') === catName);
              const categoryLikes = categoryPosts.reduce((sum, p) => sum + (p.likes?.length || 0), 0);

              return (
                <div
                  key={catName}
                  className={`bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md hover:border-emerald-200 transition-all duration-200 overflow-hidden group ${isExpanded ? 'ring-2 ring-emerald-500 ring-offset-2' : ''}`}
                >
                  <div
                    className="p-5 cursor-pointer"
                    onClick={(e) => handleCategoryCardClick(catName, e)}
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-50 to-emerald-100 border border-emerald-200 flex items-center justify-center flex-shrink-0 text-xl group-hover:from-emerald-100 group-hover:to-emerald-200 transition-colors">
                        {category.image ? (
                          <img src={category.image} alt={catName} className="w-full h-full object-cover rounded-xl" />
                        ) : (
                          <span>{getCategoryIcon(catName)}</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-gray-900 truncate">{catName}</h3>
                          {isCustom && user?.role === 'admin' && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); handleDeleteCategory(catName); }}
                              className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-50 transition-all"
                              title="Delete"
                            >
                              <Trash2 className="h-3.5 w-3.5 text-red-500" />
                            </button>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 line-clamp-2">
                          {category.description?.trim() || getCategoryDescription(catName)}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
                      <span className="flex items-center gap-1">
                        <MessageSquare className="h-3.5 w-3.5" />
                        {Math.max(0, category.count || 0)} threads
                      </span>
                      <span className="flex items-center gap-1">
                        <Heart className="h-3.5 w-3.5" />
                        {categoryLikes} likes
                      </span>
                    </div>

                    {lastPost && (
                      <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
                        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-emerald-400 to-blue-500 flex items-center justify-center flex-shrink-0">
                          <span className="text-[10px] font-bold text-white">{lastPost.author?.name?.charAt(0).toUpperCase() || '?'}</span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-medium text-gray-700 truncate">{lastPost.author?.name || 'Unknown'}</p>
                          <p className="text-[11px] text-gray-400">{formatTimeAgo(lastPost.createdAt)}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Quick Post Button */}
                  {user && !isExpanded && (
                    <div className="px-5 pb-4">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleCardCreatePost(catName); }}
                        className="create-post-button w-full flex items-center justify-center gap-2 py-2 text-sm text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors font-medium"
                      >
                        <Plus className="h-4 w-4" />
                        Quick Post
                      </button>
                    </div>
                  )}

                  {/* Inline Post Form */}
                  {isExpanded && user && (
                    <div className="create-post-form border-t border-gray-100 p-4 bg-gray-50/50" onClick={(e) => e.stopPropagation()}>
                      <div className="space-y-3">
                        <ReactQuillEditor
                          value={cardPostContent[catName] || ''}
                          onChange={(value) => setCardPostContent(prev => ({ ...prev, [catName]: value }))}
                          placeholder="What's on your mind?"
                          className="bg-white rounded-lg"
                          style={{ minHeight: '150px' }}
                        />
                        <div>
                          <div className="flex items-center space-x-2">
                            <input
                              type="text"
                              value={cardTagInput[catName] || ''}
                              onChange={(e) => setCardTagInput(prev => ({ ...prev, [catName]: e.target.value }))}
                              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCardTag(catName); } }}
                              placeholder="Add tags (press Enter)"
                              className="flex-1 px-3 py-1.5 border border-gray-200 rounded-lg text-xs focus:ring-2 focus:ring-emerald-500"
                            />
                            <button type="button" onClick={() => addCardTag(catName)} className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-xs">Add</button>
                          </div>
                          {(cardPostTags[catName] || []).length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {(cardPostTags[catName] || []).map((tag, idx) => (
                                <span key={idx} className="inline-flex items-center px-2.5 py-0.5 bg-emerald-50 text-emerald-700 rounded-full text-xs border border-emerald-200">
                                  #{tag}
                                  <button onClick={() => removeCardTag(catName, tag)} className="ml-1 hover:text-emerald-900"><X className="h-2.5 w-2.5" /></button>
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div>
                          <input type="file" id={`card-image-${catName}`} accept="image/*" multiple onChange={(e) => handleCardImageUpload(catName, e)} className="hidden" />
                          <label htmlFor={`card-image-${catName}`} className="flex items-center text-xs text-gray-500 hover:text-emerald-600 cursor-pointer">
                            <ImageIcon className="h-3.5 w-3.5 mr-1" /> Add photos
                          </label>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              setExpandedCardCategory(null);
                              setCardPostContent(prev => ({ ...prev, [catName]: '' }));
                              setCardPostImages(prev => ({ ...prev, [catName]: [] }));
                              setCardPostTags(prev => ({ ...prev, [catName]: [] }));
                              setCardTagInput(prev => ({ ...prev, [catName]: '' }));
                            }}
                            className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 text-xs"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => handleCreateCardPost(catName)}
                            disabled={creatingCardPost === catName || (!(cardPostContent[catName] || '').replace(/<[^>]*>/g, '').trim() && (cardPostImages[catName] || []).length === 0)}
                            className="flex-1 px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 text-xs flex items-center justify-center font-medium"
                          >
                            {creatingCardPost === catName ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Posting...</> : <><Send className="h-3 w-3 mr-1" /> Post</>}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {user && !showCreatePost && (
          <div className="mt-8">
            <button
              onClick={() => setShowCreatePost(true)}
              className="w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-semibold rounded-xl shadow-md shadow-emerald-200 hover:from-emerald-600 hover:to-emerald-700 hover:shadow-lg hover:shadow-emerald-300 transition-all"
            >
              <Plus className="h-5 w-5" />
              Create New Post
            </button>
          </div>
        )}

        {showCreatePost && user && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mt-6">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-sm">
                  <span className="text-sm font-bold text-white">{user.name?.charAt(0).toUpperCase()}</span>
                </div>
              </div>
              <div className="flex-1">
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={postCategory}
                    onChange={(e) => setPostCategory(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  >
                    {categories.map((cat) => (
                      <option key={cat.name} value={cat.name}>{cat.name} {cat.count > 0 ? `(${cat.count})` : ''}</option>
                    ))}
                  </select>
                </div>
                <ReactQuillEditor value={postContent} onChange={setPostContent} placeholder="What's on your mind?" className="bg-white rounded-lg" style={{ minHeight: '200px' }} />
                <div className="mt-3">
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                      placeholder="Add tags (press Enter)"
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500"
                    />
                    <button onClick={addTag} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 text-sm">Add</button>
                  </div>
                  {postTags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {postTags.map((tag, idx) => (
                        <span key={idx} className="inline-flex items-center px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-sm border border-emerald-200">
                          #{tag}
                          <button onClick={() => removeTag(tag)} className="ml-2 hover:text-emerald-900"><X className="h-3 w-3" /></button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                {postImages.length > 0 && (
                  <div className="mt-4 grid grid-cols-2 gap-2">
                    {postImages.map((image, idx) => (
                      <div key={idx} className="relative group">
                        <img src={image} alt={`Preview ${idx + 1}`} className="w-full h-32 object-cover rounded-lg" />
                        <button onClick={() => setPostImages(prev => prev.filter((_, i) => i !== idx))} className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex items-center justify-between mt-4">
                  <input type="file" ref={fileInputRef} onChange={handleImageUpload} accept="image/*" multiple className="hidden" />
                  <button type="button" onClick={() => fileInputRef.current?.click()} className="flex items-center text-gray-500 hover:text-emerald-600 transition-colors">
                    <ImageIcon className="h-5 w-5 mr-2" /> Photo
                  </button>
                  <div className="flex gap-3">
                    <button onClick={() => { setShowCreatePost(false); setPostContent(''); setPostTags([]); setPostImages([]); setPostCategory('General'); }} className="px-4 py-2 border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50 text-sm">Cancel</button>
                    <button
                      onClick={handleCreatePost}
                      disabled={creating || (!postContent.replace(/<[^>]*>/g, '').trim() && postImages.length === 0)}
                      className="px-5 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 disabled:opacity-50 flex items-center text-sm font-medium shadow-sm"
                    >
                      {creating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Posting...</> : <><Send className="h-4 w-4 mr-2" /> Post</>}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Forum;
